const CART_UPSELL_STORAGE_KEY = 'cartUpsellProductId';
const CART_UPSELL_LIMIT = 4;
const CART_UPSELL_AUTOPLAY_INTERVAL = 5000;
const CART_UPSELL_ANIMATION_DURATION = 200;


/*
 * Mantém salvo o último produto adicionado.
 *
 * Esse listener global é importante porque
 * quando o carrinho está vazio ainda pode
 * não existir um <cart-upsell> no DOM.
 */
document.addEventListener('cart:update', (event) => {
  const productId =
    event.detail?.data?.productId;

  if (!productId) return;

  sessionStorage.setItem(
    CART_UPSELL_STORAGE_KEY,
    String(productId)
  );
});


class CartUpsell extends HTMLElement {

  connectedCallback() {
    this.content = this.querySelector(
      '[data-cart-upsell-content]'
    );

    this.counter = this.querySelector(
      '[data-cart-upsell-counter]'
    );

    this.previousButton = this.querySelector(
      '[data-cart-upsell-prev]'
    );

    this.nextButton = this.querySelector(
      '[data-cart-upsell-next]'
    );


    this.cards = [];
    this.currentIndex = 0;

    this.autoPlayTimer = null;
    this.isAnimating = false;


    this.previousButton?.addEventListener(
      'click',
      this.handlePrevious
    );

    this.nextButton?.addEventListener(
      'click',
      this.handleNext
    );


    document.addEventListener(
      'cart:update',
      this.handleCartUpdate
    );


    /*
     * IMPORTANTE:
     *
     * Não dependemos mais apenas
     * do sessionStorage ao carregar.
     *
     * Consultamos o carrinho real.
     */
    this.syncWithCart();
  }


  disconnectedCallback() {
    document.removeEventListener(
      'cart:update',
      this.handleCartUpdate
    );


    this.previousButton?.removeEventListener(
      'click',
      this.handlePrevious
    );


    this.nextButton?.removeEventListener(
      'click',
      this.handleNext
    );


    this.stopAutoPlay();
  }


  /*
   * Toda alteração do carrinho passa
   * novamente pela sincronização.
   *
   * Se o evento possuir productId,
   * significa normalmente que houve
   * uma adição e damos preferência
   * para esse produto.
   *
   * Se não possuir productId,
   * como em uma remoção, consultamos
   * o estado atual do carrinho.
   */
  handleCartUpdate = (event) => {
    const productId =
      event.detail?.data?.productId;


    this.syncWithCart(
      productId || null
    );
  };


  /*
   * Sincroniza o upsell com o estado
   * REAL do carrinho.
   */
  async syncWithCart(
    preferredProductId = null
  ) {

    const cart =
      await this.getCart();


    /*
     * Não conseguimos consultar
     * o carrinho.
     */
    if (!cart) {
      return;
    }


    /*
     * Carrinho vazio:
     * não existe motivo para mostrar upsell.
     */
    if (
      !cart.items ||
      cart.items.length === 0
    ) {

      sessionStorage.removeItem(
        CART_UPSELL_STORAGE_KEY
      );

      this.stopAutoPlay();

      this.cards = [];
      this.currentIndex = 0;

      if (this.content) {
        this.content.innerHTML = '';
      }

      this.hidden = true;

      return;
    }


    const cartProductIds =
      cart.items.map(
        (item) =>
          String(item.product_id)
      );


    /*
     * Ordem de prioridade para definir
     * qual produto será usado como referência:
     *
     * 1. produto acabado de adicionar;
     * 2. último produto salvo;
     * 3. algum produto atual do carrinho.
     */
    let referenceProductId =
      preferredProductId
        ? String(preferredProductId)
        : sessionStorage.getItem(
            CART_UPSELL_STORAGE_KEY
          );


    /*
     * Se a referência não existir mais
     * no carrinho, por exemplo porque
     * o usuário removeu justamente
     * aquele produto, escolhemos outro.
     */
    if (
      !referenceProductId ||
      !cartProductIds.includes(
        String(referenceProductId)
      )
    ) {

      referenceProductId =
        String(
          cart.items[0].product_id
        );
    }


    /*
     * Mantém a referência válida
     * para próximos reloads.
     */
    sessionStorage.setItem(
      CART_UPSELL_STORAGE_KEY,
      referenceProductId
    );


    await this.loadRecommendations(
      referenceProductId,
      cartProductIds
    );
  }


  /*
   * Produto anterior.
   */
  handlePrevious = () => {
    if (
      this.cards.length <= 1 ||
      this.isAnimating
    ) {
      return;
    }


    let nextIndex =
      this.currentIndex - 1;


    if (nextIndex < 0) {
      nextIndex =
        this.cards.length - 1;
    }


    this.changeCard(
      nextIndex,
      'prev'
    );
  };


  /*
   * Próximo produto.
   *
   * Também utilizado pelo autoplay.
   */
  handleNext = () => {
    if (
      this.cards.length <= 1 ||
      this.isAnimating
    ) {
      return;
    }


    let nextIndex =
      this.currentIndex + 1;


    if (
      nextIndex >=
      this.cards.length
    ) {
      nextIndex = 0;
    }


    this.changeCard(
      nextIndex,
      'next'
    );
  };


  /*
   * Animação de troca dos produtos.
   */
  changeCard(
    nextIndex,
    direction = 'next'
  ) {

    if (
      !this.content ||
      this.isAnimating
    ) {
      return;
    }


    this.isAnimating = true;


    const leavingClass =
      direction === 'next'
        ? 'is-leaving-next'
        : 'is-leaving-prev';


    const enteringClass =
      direction === 'next'
        ? 'is-entering-next'
        : 'is-entering-prev';


    /*
     * Produto atual sai.
     */
    this.content.classList.add(
      leavingClass
    );


    setTimeout(() => {

      this.currentIndex =
        nextIndex;


      this.renderCurrentCard();


      this.content.classList.remove(
        leavingClass
      );


      /*
       * Novo produto começa fora
       * da área visível.
       */
      this.content.classList.add(
        enteringClass
      );


      /*
       * Força o navegador a registrar
       * a posição inicial.
       */
      void this.content.offsetWidth;


      requestAnimationFrame(() => {

        /*
         * Novo produto entra.
         */
        this.content.classList.remove(
          enteringClass
        );


        setTimeout(() => {
          this.isAnimating = false;
        }, CART_UPSELL_ANIMATION_DURATION);

      });

    }, CART_UPSELL_ANIMATION_DURATION);
  }


  /*
   * Inicia autoplay.
   */
  startAutoPlay() {
    this.stopAutoPlay();


    if (
      !this.cards ||
      this.cards.length <= 1
    ) {
      return;
    }


    this.autoPlayTimer =
      setInterval(
        () => {
          this.handleNext();
        },
        CART_UPSELL_AUTOPLAY_INTERVAL
      );
  }


  /*
   * Para autoplay.
   */
  stopAutoPlay() {
    if (!this.autoPlayTimer) {
      return;
    }


    clearInterval(
      this.autoPlayTimer
    );


    this.autoPlayTimer = null;
  }


  /*
   * Exibe somente o produto atual.
   */
  renderCurrentCard() {
    if (!this.cards.length) return;


    this.cards.forEach(
      (card, index) => {

        card.hidden =
          index !== this.currentIndex;

      }
    );


    if (this.counter) {

      this.counter.textContent =
        `${this.currentIndex + 1}/${this.cards.length}`;

    }


    const disableNavigation =
      this.cards.length <= 1;


    if (this.previousButton) {

      this.previousButton.disabled =
        disableNavigation;

    }


    if (this.nextButton) {

      this.nextButton.disabled =
        disableNavigation;

    }
  }


  /*
   * Carrega recomendações.
   *
   * cartProductIds pode ser recebido
   * diretamente do syncWithCart para
   * evitar consultar /cart.js novamente.
   */
  async loadRecommendations(
    productId,
    cartProductIds = null
  ) {

    if (!this.content) return;


    this.stopAutoPlay();


    this.isAnimating = false;


    this.content.classList.remove(
      'is-leaving-next',
      'is-entering-next',
      'is-leaving-prev',
      'is-entering-prev'
    );


    this.hidden = true;


    let cards = [];


    /*
     * Se loadRecommendations for chamado
     * diretamente por algum motivo,
     * ainda conseguimos consultar
     * os produtos do carrinho.
     */
    if (!cartProductIds) {

      cartProductIds =
        await this.getCartProductIds();

    }


    /*
     * 1. COMPLEMENTARY
     */
    const complementary =
      await this.fetchRecommendationCards(
        productId,
        'complementary'
      );


    cards.push(
      ...complementary
    );


    /*
     * 2. RELATED
     */
    if (
      cards.length <
      CART_UPSELL_LIMIT
    ) {

      const related =
        await this.fetchRecommendationCards(
          productId,
          'related'
        );


      cards.push(
        ...related
      );
    }


    /*
     * Remove:
     *
     * - produto de referência;
     * - produtos já no carrinho;
     * - duplicados.
     */
    cards =
      this.filterCards(
        cards,
        productId,
        cartProductIds
      );


    /*
     * 3. FALLBACK
     */
    if (
      cards.length <
      CART_UPSELL_LIMIT
    ) {

      let fallbackCards =
        await this.fetchFallbackCards();


      fallbackCards =
        this.shuffleArray(
          fallbackCards
        );


      cards.push(
        ...fallbackCards
      );


      cards =
        this.filterCards(
          cards,
          productId,
          cartProductIds
        );
    }


    /*
     * Máximo de 4 produtos.
     */
    cards =
      cards.slice(
        0,
        CART_UPSELL_LIMIT
      );


    /*
     * Nenhum produto disponível.
     */
    if (!cards.length) {

      this.cards = [];
      this.currentIndex = 0;

      this.content.innerHTML = '';

      this.hidden = true;

      return;
    }


    /*
     * Insere cards.
     */
    this.content.innerHTML =
      cards
        .map(
          (card) =>
            card.outerHTML
        )
        .join('');


    this.cards =
      Array.from(
        this.content.querySelectorAll(
          '[data-cart-upsell-card]'
        )
      );


    this.currentIndex = 0;


    this.renderCurrentCard();


    this.hidden = false;


    this.startAutoPlay();
  }


  /*
   * Busca recommendations da Shopify.
   */
  async fetchRecommendationCards(
    productId,
    intent
  ) {

    try {

      const baseUrl =
        this.dataset.recommendationsUrl;


      if (!baseUrl) {
        return [];
      }


      const url =
        `${baseUrl}` +
        `?section_id=cart-upsell-recommendations` +
        `&product_id=${encodeURIComponent(productId)}` +
        `&limit=${CART_UPSELL_LIMIT}` +
        `&intent=${encodeURIComponent(intent)}`;


      const response =
        await fetch(url);


      if (!response.ok) {
        return [];
      }


      const htmlText =
        await response.text();


      const documentFragment =
        new DOMParser()
          .parseFromString(
            htmlText,
            'text/html'
          );


      const results =
        documentFragment
          .querySelector(
            '[data-cart-upsell-results]'
          );


      if (!results) {
        return [];
      }


      return Array.from(
        results.querySelectorAll(
          '[data-cart-upsell-card]'
        )
      );

    }
    catch (error) {

      console.error(
        `Erro ao carregar recomendações ${intent}:`,
        error
      );


      return [];
    }
  }


  /*
   * Busca fallback.
   */
  async fetchFallbackCards() {

    try {

      const root =
        window.Shopify?.routes?.root ||
        '/';


      const response =
        await fetch(
          `${root}?section_id=cart-upsell-fallback`
        );


      if (!response.ok) {
        return [];
      }


      const htmlText =
        await response.text();


      const documentFragment =
        new DOMParser()
          .parseFromString(
            htmlText,
            'text/html'
          );


      const results =
        documentFragment
          .querySelector(
            '[data-cart-upsell-fallback-results]'
          );


      if (!results) {
        return [];
      }


      return Array.from(
        results.querySelectorAll(
          '[data-cart-upsell-card]'
        )
      );

    }
    catch (error) {

      console.error(
        'Erro ao carregar fallback do upsell:',
        error
      );


      return [];
    }
  }


  /*
   * Retorna o carrinho completo.
   */
  async getCart() {

    try {

      const root =
        window.Shopify?.routes?.root ||
        '/';


      const response =
        await fetch(
          `${root}cart.js`
        );


      if (!response.ok) {
        return null;
      }


      return await response.json();

    }
    catch (error) {

      console.error(
        'Erro ao consultar carrinho:',
        error
      );


      return null;
    }
  }


  /*
   * Retorna apenas os product IDs
   * existentes no carrinho.
   */
  async getCartProductIds() {

    const cart =
      await this.getCart();


    if (
      !cart ||
      !cart.items
    ) {
      return [];
    }


    return cart.items.map(
      (item) =>
        String(item.product_id)
    );
  }


  /*
   * Remove produtos que não devem
   * aparecer no upsell.
   */
  filterCards(
    cards,
    currentProductId,
    cartProductIds
  ) {

    const seen =
      new Set();


    return cards.filter(
      (card) => {

        const productId =
          String(
            card.dataset.productId ||
            ''
          );


        if (!productId) {
          return false;
        }


        /*
         * Produto de referência.
         */
        if (
          productId ===
          String(currentProductId)
        ) {
          return false;
        }


        /*
         * Produto já no carrinho.
         */
        if (
          cartProductIds.includes(
            productId
          )
        ) {
          return false;
        }


        /*
         * Duplicado.
         */
        if (
          seen.has(productId)
        ) {
          return false;
        }


        seen.add(
          productId
        );


        return true;
      }
    );
  }


  /*
   * Fisher-Yates shuffle.
   */
  shuffleArray(array) {

    const shuffled =
      [...array];


    for (
      let i =
        shuffled.length - 1;

      i > 0;

      i--
    ) {

      const randomIndex =
        Math.floor(
          Math.random() *
          (i + 1)
        );


      [
        shuffled[i],
        shuffled[randomIndex]
      ] = [
        shuffled[randomIndex],
        shuffled[i]
      ];
    }


    return shuffled;
  }

}


if (
  !customElements.get(
    'cart-upsell'
  )
) {

  customElements.define(
    'cart-upsell',
    CartUpsell
  );

}